import { useMemo } from "react";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { GET_CLIMBER_BY_ID } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { useClimberProgress } from "./useClimbersProgress";
import type { Climber } from "../types/mmp-graphql";

interface ClimberByIdResponse {
  getClimberById: Climber[];
}

export interface ClimberWithUserPath extends Omit<Climber, "id" | "path"> {
  userID: string;
  userPath?: string;
  role?: string;
  riasec?: unknown;
  uniqueIdentifiers?: Array<{
    sectionID?: string | null;
    userPath?: string;
  }>;
}

interface QueryKey {
  id: string;
}

const fetchClimberById = async ({
  queryKey,
}: {
  queryKey: [string, QueryKey];
}): Promise<ClimberWithUserPath> => {
  const [, { id }] = queryKey;
  const { getClimberById } = await graphQLClient.request<ClimberByIdResponse>(
    GET_CLIMBER_BY_ID,
    {
      id,
    },
  );
  const climber = getClimberById[0];

  const userObj: ClimberWithUserPath = {
    userID: climber.id,
    sub: climber.sub,
    firstName: climber.firstName,
    lastName: climber.lastName,
    username: climber.username,
    email: climber.email,
    shardID: climber.shardID,
    xp: climber.xp,
    preferredID: climber.preferredID,
    userPath: climber.path,
    receivedSurvey: climber.receivedSurvey,
    // Legacy progress fields off the climber record — still selected by
    // GET_CLIMBER_BY_ID as a fallback for climbers not yet migrated to prg-.
    profileChoices: climber.profileChoices,
    careerPlan: climber.careerPlan,
    coursePlan: climber.coursePlan,
    uniqueIdentifiers: getClimberById.map(({ parentID, path }) => ({
      sectionID: parentID,
      userPath: path,
    })),
  };
  return userObj;
};

// Progress (facets, profileChoices, ladder/career/course plans) now lives on the
// prg- record rather than the climber record, so staff/teacher/admin views fetch
// it separately and merge it onto the climber. getClimbersProgress merges every
// product's record, so the viewer's product needn't match the climber's.
export const useGetClimberById = (
  id: string | undefined,
  enabled: boolean = true,
): UseQueryResult<ClimberWithUserPath, Error> => {
  const canFetch = !!id && enabled;

  const climberQuery = useQuery({
    queryKey: ["climber", { id: id || "" }],
    queryFn: fetchClimberById,
    enabled: canFetch,
  });

  const climber = climberQuery.data;

  // Shares the cache entry with useClimbersProgress, so the other consumers on
  // the climber-detail page don't each issue their own identical request.
  const {
    progress,
    loading: progressLoading,
    error: progressError,
  } = useClimberProgress(canFetch ? id : undefined);

  const data = useMemo<ClimberWithUserPath | undefined>(() => {
    if (!climber) return climber;
    // Prefer the prg- record; fall back to the climber record so legacy
    // climbers whose data hasn't been migrated yet still render.
    return {
      ...climber,
      ladderProgress: progress?.ladderProgress ?? climber.ladderProgress,
      facets: progress?.facets ?? climber.facets,
      profileChoices: progress?.profileChoices ?? climber.profileChoices,
      careerPlan: progress?.careerPlan ?? climber.careerPlan,
      coursePlan: progress?.coursePlan ?? climber.coursePlan,
    };
  }, [climber, progress]);

  return {
    ...climberQuery,
    data,
    isLoading: climberQuery.isLoading || progressLoading,
    // Surface progress-fetch failures too, so a prg- error isn't silently
    // hidden behind the (fallback) climber-record data.
    isError: climberQuery.isError || !!progressError,
    error: climberQuery.error ?? progressError,
  } as UseQueryResult<ClimberWithUserPath, Error>;
};
